import { useEffect, useState } from 'react';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

/**
 * Whether the signed-in person is a sandbox admin, so the sidebar can offer the Sandbox admin app (`/sandbox/admin/`,
 * vfs-service src/gateway/admin). The sandbox gateway answers `GET /sandbox/admin/api/me` with 200 and
 * `{ sub, email, name, mode }` for an admin, 403 for anyone else signed in and 401 without a sign-in; the admin app
 * itself calls the same `./api/me`, same origin with credentials (envoy turns the Flow session cookie into a token).
 *
 * Asked once per page and person (later callers share the answer) and fails closed: anything but a 200 with a `sub`,
 * an error or no answer yet all mean "not an admin". Never blocks rendering.
 */
export const SANDBOX_ADMIN_URL = '/flow/admin/';
const ME_URL = '/sandbox/admin/api/me';

let cache = { key: null, promise: null, value: false };

/** Resolves to `true` only for a sandbox admin. */
export function checkSandboxAdmin(key = 'me') {
  if (cache.key === key && cache.promise) { return cache.promise; }
  const entry = { key, promise: null, value: false };
  entry.promise = Promise.resolve()
    .then(() => getAuthorizedFetch()(ME_URL, { credentials: 'include', headers: { Accept: 'application/json' } }))
    .then(async(response) => {
      if (response?.status !== 200) { return false; }
      const me = await response.json();
      return Boolean(me && me.sub);
    })
    .catch(() => false)
    .then((admin) => { entry.value = admin; return admin; });
  cache = entry;
  return entry.promise;
}

/** For tests: forget the answer. */
export function resetSandboxAdmin() {
  cache = { key: null, promise: null, value: false };
}

/**
 * `true` once the gateway confirmed the person is a sandbox admin. `userKey` is who is signed in (nothing is asked
 * without one); `enabled: false` skips the check (no sandbox gateway).
 */
export default function useSandboxAdmin(userKey, { enabled = true } = {}) {
  const active = Boolean(enabled && userKey);
  const [admin, setAdmin] = useState(() => active && cache.key === userKey && cache.value);

  useEffect(() => {
    if (!active) { setAdmin(false); return undefined; }
    let alive = true;
    checkSandboxAdmin(userKey).then((value) => { if (alive) { setAdmin(value); } });
    return () => { alive = false; };
  }, [active, userKey]);

  return active && admin;
}
